import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@lib/components/Modal/Modal';
import Form, { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import formStyles from '@lib/assets/styles/Form.module.scss';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import { FieldItem, Values } from '@lib/interfaces/form';
import DocumentListFilterActions from './DocumentListFilterActions';
import { DOCUMENT_LIST_FILTER_EMPTY_VALUES } from '../utils/useDocumentFilterFields';
import styles from './DocumentListFilter.module.scss';

type DocumentListFilterProps = {
  clearFilter: () => void;
  closeFilter: () => void;
  favoritesDisabled?: boolean;
  filterCounter: number;
  filterDisabled?: boolean;
  filterFields: FieldItem[];
  filterParams: { [key: string]: unknown };
  isFavoritedMode: boolean;
  isFilterModalOpen: boolean;
  isFullAdmin?: boolean;
  isLocked?: boolean;
  onFilterSubmit: (values: Values) => void;
  onToggleFavoritedMode: () => void;
  onUpgrade?: () => void;
  openFilter: () => void;
  isApprovals?: boolean;
};

function DocumentListFilter({
  clearFilter,
  closeFilter,
  favoritesDisabled,
  filterCounter,
  filterDisabled,
  filterFields,
  filterParams,
  isFavoritedMode,
  isFilterModalOpen,
  isFullAdmin,
  isLocked,
  onFilterSubmit,
  onToggleFavoritedMode,
  onUpgrade,
  openFilter,
  isApprovals,
}: DocumentListFilterProps) {
  const { t } = useTranslation();
  const formRef = useRef<FormRef>(undefined);

  const defaultFilterValues =
    filterParams && Object.keys(filterParams).length > 0
      ? filterParams
      : DOCUMENT_LIST_FILTER_EMPTY_VALUES;

  // When the EDM plan is locked, the locked variants of these chips are
  // pure upgrade CTAs. Only Full Admins can act on them, so hide the chips
  // outright for non-Full-Admin Users instead of showing a dead-end button.
  const hideLockedChips = !!isLocked && !isFullAdmin;
  const showFavoritesChip = !isApprovals && !hideLockedChips;
  const showFilterChip = !hideLockedChips;
  // With both chips hidden the filter row becomes an empty WhiteBox shell;
  // skip rendering it entirely so non-Full-Admin Users on a locked plan
  // don't see an empty container above the table.
  const showFilterBar = showFavoritesChip || showFilterChip;

  return (
    <>
      {showFilterBar && (
        <div className={styles.filterWrap}>
          <WhiteBox
            className={styles.filter}
            verticalPaddings="extraSmall"
            horizontalPaddings="small"
          >
            <div className={styles.filterRow}>
              {showFavoritesChip && (
                <Chip
                  leftIcon={isLocked ? 'lock' : 'bookmark_star'}
                  leftIconSymbolsOutlined
                  leftIconFilled={!isLocked}
                  label={t('favorites')}
                  onClick={
                    // eslint-disable-next-line no-nested-ternary
                    isLocked
                      ? onUpgrade
                      : favoritesDisabled
                        ? undefined
                        : onToggleFavoritedMode
                  }
                  type={
                    isLocked || favoritesDisabled
                      ? ChipTypes.chipDisabled
                      : ChipTypes.chipFilter
                  }
                  size={ChipSizes.large}
                  rightIcon={
                    !isLocked && isFavoritedMode ? 'check_circled' : undefined
                  }
                  rightIconClassName={styles.blueIcon}
                  tooltip={isLocked ? t('unlock-favorites-tooltip') : undefined}
                />
              )}
              {showFilterChip && (
                <Chip
                  counter={isLocked ? undefined : filterCounter}
                  label={t('filter')}
                  leftIcon={isLocked ? 'lock' : 'filter_alt'}
                  leftIconSymbolsOutlined
                  onClick={
                    // eslint-disable-next-line no-nested-ternary
                    isLocked
                      ? onUpgrade
                      : filterDisabled
                        ? undefined
                        : openFilter
                  }
                  size={ChipSizes.large}
                  type={
                    // eslint-disable-next-line no-nested-ternary
                    isLocked || filterDisabled
                      ? ChipTypes.chipDisabled
                      : ChipTypes.chipFilter
                  }
                  tooltip={isLocked ? t('unlock-filter-tooltip') : undefined}
                  tooltipForCroppedTextDisabled
                />
              )}
              {!!filterCounter && (
                <Button
                  buttonSize={ButtonSizes.large}
                  buttonText={t('reset')}
                  buttonType={ButtonTypes.primaryText}
                  onClick={clearFilter}
                />
              )}
            </div>
          </WhiteBox>
        </div>
      )}
      <Modal
        isOpen={isFilterModalOpen}
        caption={t('filter')}
        onClose={closeFilter}
      >
        <Form
          formRef={formRef}
          fields={filterFields}
          onSubmit={onFilterSubmit}
          defaultValues={defaultFilterValues}
          actionsComponent={DocumentListFilterActions}
          actionsComponentProps={{
            onCancel: closeFilter,
          }}
          formClassName={formStyles.form}
        />
      </Modal>
    </>
  );
}

export default DocumentListFilter;
